import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useUpdateBillingRateCardMutation } from '../hooks/useUpdateBillingRateCardMutation'
import { rateInput } from '../lib/numberInput'
import { currentUtcMonth } from '../lib/periods'
import { changeMovesSomething } from '../lib/rates'
import {
  RATE_FIELDS,
  type FtweTier,
  type RateCardChange,
  type RateCardChangeRequest,
  type RateCardSnapshot,
  type RateField,
} from '../types'
import { DayField } from './DayField'
import { FtweTierField } from './FtweTierField'
import { RateCardChangePreview } from './RateCardChangePreview'

interface ChangeRateCardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The org's card in force today, shown as each field's hint. */
  rateCard: RateCardSnapshot | null
}

/** Lets Careforce staff preview, then write, a change to the organization's
 *  rate card from a day they pick: today (UTC) unless they pick a later one. */
export function ChangeRateCardDialog({
  open,
  onOpenChange,
  rateCard,
}: ChangeRateCardDialogProps) {
  // Closing mid-write would unmount the form before it can show the outcome.
  const [writing, setWriting] = useState(false)
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && writing) return
        onOpenChange(next)
      }}>
      <DialogContent
        showCloseButton={!writing}
        className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Change rate card</DialogTitle>
          <DialogDescription>
            Fields left blank carry over from the latest card, which may be one
            already scheduled; the preview lists every rate before anything is
            written.
          </DialogDescription>
        </DialogHeader>
        <ChangeRateCardForm
          rateCard={rateCard}
          writing={writing}
          setWriting={setWriting}
          onDone={() => {
            setWriting(false)
            onOpenChange(false)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

type RateInputs = Record<RateField, string>

const BLANK_INPUTS: RateInputs = {
  minute_rate: '',
  non_conversation_discount: '',
  reasoning_multiplier: '',
  computer_use_multiplier: '',
  david_session_multiplier: '',
  document_rate: '',
  rubric_eval_multiplier: '',
}

interface Preview {
  request: RateCardChangeRequest
  change: RateCardChange
}

// What a change to each rate reaches, so staff can tell which are safe to
// change mid-month. Multipliers price each call or session as it happens; the
// minute rate and discount also price Connection, billed once at month end.
const RATE_HINTS: Record<RateField, string> = {
  document_rate:
    'Charged once when a signing document is created, even if unsigned. Template previews are excluded.',
  minute_rate:
    "Calls switch on the start day. That month's whole Connection charge is billed at the new rate.",
  non_conversation_discount:
    "Reprices the start month's whole Connection charge, days before the start included.",
  reasoning_multiplier: 'Applies from the start day. Safe to change any day.',
  computer_use_multiplier:
    'Applies from the start day. Safe to change any day.',
  // Hoku prices David sessions from a card each server caches for 2 minutes.
  david_session_multiplier:
    'Applies within 2 minutes of the start. Safe to change any day.',
  rubric_eval_multiplier:
    'Applies to calls graded from the start day. Safe to change any day.',
}

// The rate columns are numeric(10,4): up to 4 decimal places and 6 digits
// before the point. Relay refuses a rate that breaks these rules; the dialog
// says so before the preview is asked for.
const RATE_DECIMAL_PLACES = 4
const RATE_LIMIT = 1_000_000

/** Why a filled-in rate, or a custom FTWE multiplier, cannot be previewed, or
 *  null when it can. The field takes only digits and a point, so a rate is
 *  never negative. */
function rateProblem(
  key: RateField | 'ftwe_multiplier',
  value: string
): string | null {
  const rate = Number(value)
  if (!Number.isFinite(rate)) return 'Enter a number.'
  // "0998899" reads as a typo for some other number, not as 998899.
  if (/^0\d/.test(value)) return 'Remove the leading zero.'
  const places = value.split('.')[1]?.length ?? 0
  if (places > RATE_DECIMAL_PLACES)
    return `Up to ${RATE_DECIMAL_PLACES} decimal places.`
  if (rate >= RATE_LIMIT)
    return 'Must be below 1,000,000, the most a rate card can store.'
  if ((key === 'minute_rate' || key === 'ftwe_multiplier') && rate <= 0)
    return 'Above 0.'
  if (key === 'non_conversation_discount' && rate >= 1)
    return 'Below 1. A discount of 1 waives the charge.'
  if (key === 'computer_use_multiplier' && rate > 1) return 'Between 0 and 1.'
  return null
}

function ChangeRateCardForm({
  rateCard,
  writing,
  setWriting,
  onDone,
}: {
  rateCard: RateCardSnapshot | null
  /** A write is in flight; held by the dialog, which stays open meanwhile. */
  writing: boolean
  setWriting: (writing: boolean) => void
  onDone: () => void
}) {
  const mutation = useUpdateBillingRateCardMutation()
  const today = currentUtcMonth().today
  // Today unless a later day is picked: a later day closes the card in force
  // off then, or corrects a change already scheduled from that day.
  const [startsOn, setStartsOn] = useState(today)
  const [inputs, setInputs] = useState<RateInputs>(BLANK_INPUTS)
  // '' leaves the card's FTWE setting as it is.
  const [ftweTier, setFtweTier] = useState<FtweTier | ''>('')
  const [ftweMultiplier, setFtweMultiplier] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const busy = mutation.isPending || writing

  // Any edit invalidates the preview, so Apply change only writes what was
  // previewed.
  function edit(change: () => void) {
    change()
    setPreview(null)
    setError(null)
  }

  const filled = RATE_FIELDS.filter(({ key }) => inputs[key] !== '')
  const invalid = filled.filter(
    ({ key }) => rateProblem(key, inputs[key]) !== null
  )
  const ftweMultiplierProblem =
    ftweTier === 'custom' && ftweMultiplier !== ''
      ? rateProblem('ftwe_multiplier', ftweMultiplier)
      : null
  // Custom is a tier only once the number it stands for is typed.
  const ftweIncomplete = ftweTier === 'custom' && ftweMultiplier === ''
  const canPreview =
    (filled.length > 0 || ftweTier !== '') &&
    invalid.length === 0 &&
    ftweMultiplierProblem === null &&
    !ftweIncomplete
  const trimmedReason = reason.trim()

  function previewRequest(): RateCardChangeRequest {
    const rates = Object.fromEntries(
      filled.map(({ key }) => [key, Number(inputs[key])])
    )
    return {
      effective_from: startsOn,
      ...rates,
      ...(ftweTier ? { ftwe_tier: ftweTier } : {}),
      ...(ftweTier === 'custom'
        ? { ftwe_multiplier: Number(ftweMultiplier) }
        : {}),
      ...(trimmedReason ? { note: trimmedReason } : {}),
      confirm: false,
    }
  }

  function requestPreview() {
    setError(null)
    const request = previewRequest()
    mutation.mutate(request, {
      onSuccess: (change) => setPreview({ request, change }),
      onError: (err) => setError(err.message),
    })
  }

  function apply(request: RateCardChangeRequest) {
    setWriting(true)
    setError(null)
    mutation.mutate(
      { ...request, confirm: true },
      {
        onSuccess: onDone,
        onError: (err) => {
          // The admin previews again before retrying: the failed write may
          // have landed or been reverted, and the card on screen may differ.
          setWriting(false)
          setPreview(null)
          setError(err.message)
        },
      }
    )
  }

  return (
    <div className="grid gap-4">
      {RATE_FIELDS.map(({ key, label }) => {
        const problem =
          inputs[key] === '' ? null : rateProblem(key, inputs[key])
        return (
          <div key={key} className="grid gap-2">
            <Label htmlFor={key}>{label}</Label>
            <Input
              id={key}
              inputMode="decimal"
              disabled={busy}
              placeholder={`Today: ${rateCard?.[key] ?? '—'}`}
              value={inputs[key]}
              aria-invalid={problem !== null}
              aria-describedby={`${key}-hint`}
              onChange={(e) => {
                const value = rateInput(e.target.value)
                if (value === null) return
                edit(() => setInputs((prev) => ({ ...prev, [key]: value })))
              }}
            />
            <p id={`${key}-hint`} className="text-xs text-muted-foreground">
              {RATE_HINTS[key]}
            </p>
            {problem && (
              <p className="text-xs text-error-low-contrast">{problem}</p>
            )}
          </div>
        )
      })}
      <FtweTierField
        tier={ftweTier}
        multiplier={ftweMultiplier}
        problem={ftweMultiplierProblem}
        disabled={busy}
        rateCard={rateCard}
        onTierChange={(tier) =>
          edit(() => {
            setFtweTier(tier)
            setFtweMultiplier('')
          })
        }
        onMultiplierChange={(value) => edit(() => setFtweMultiplier(value))}
      />
      <div className="grid gap-2">
        <Label htmlFor="rate-card-starts-on">Starts (UTC)</Label>
        <DayField
          id="rate-card-starts-on"
          value={startsOn}
          earliestDay={today}
          disabled={busy}
          onChange={(day) => edit(() => setStartsOn(day))}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="rate-card-reason">Reason</Label>
        <Input
          id="rate-card-reason"
          disabled={busy}
          value={reason}
          onChange={(e) => edit(() => setReason(e.target.value))}
        />
      </div>

      {preview && <RateCardChangePreview change={preview.change} />}

      {error && (
        <p role="alert" className="text-sm text-error-low-contrast">
          {error}
        </p>
      )}

      <DialogFooter>
        <Button
          variant="outline"
          disabled={!canPreview || busy}
          onClick={requestPreview}>
          Preview
        </Button>
        <Button
          disabled={!preview || busy || !changeMovesSomething(preview.change)}
          onClick={() =>
            preview &&
            apply({
              ...preview.request,
              previewed_card_version: preview.change.card_in_force_version,
            })
          }>
          Apply change
        </Button>
      </DialogFooter>
    </div>
  )
}
